import { useState, useEffect } from 'react';
import { getPendingFeedbackCount } from 'api/axios';
import { technicalNotesApi } from 'api/models';
import { useAuthContext } from 'auth/AuthProvider';

interface SidebarCounts {
  feedbackCount: number;
  technicalNotesCount: number;
}

export const useSidebarCounts = () => {
  const [counts, setCounts] = useState<SidebarCounts>({
    feedbackCount: 0,
    technicalNotesCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuthContext();

  const fetchFeedbackCount = async () => {
    try {
      const feedbackResponse = await getPendingFeedbackCount({
        agent_id: null, // As requested, pass null temporarily
        workspace_id: user?.current_workspace?.id,
      });

      setCounts(prev => ({
        ...prev,
        feedbackCount: feedbackResponse.count || 0,
      }));
    } catch (error) {
      console.error('Error fetching feedback count:', error);
      setCounts(prev => ({
        ...prev,
        feedbackCount: 0,
      }));
    }
  };

  const fetchTechnicalNotesCount = async () => {
    try {
      const technicalNotesResponse = await technicalNotesApi.getPendingCount({
        agent_id: null, // As requested, pass null temporarily
        workspace_id: user?.current_workspace?.id,
      });

      setCounts(prev => ({
        ...prev,
        technicalNotesCount: technicalNotesResponse.count || 0,
      }));
    } catch (error) {
      console.error('Error fetching technical notes count:', error);
      setCounts(prev => ({
        ...prev,
        technicalNotesCount: 0,
      }));
    }
  };

  const fetchAllCounts = async () => {
    try {
      setLoading(true);
      
      // Fetch both counts in parallel
      const [feedbackResponse, technicalNotesResponse] = await Promise.all([
        getPendingFeedbackCount({
          agent_id: null, // As requested, pass null temporarily
          workspace_id: user?.current_workspace?.id,
        }),
        technicalNotesApi.getPendingCount({
          agent_id: null, // As requested, pass null temporarily
          workspace_id: user?.current_workspace?.id,
        }),
      ]);

      setCounts({
        feedbackCount: feedbackResponse.count || 0,
        technicalNotesCount: technicalNotesResponse.count || 0,
      });
    } catch (error) {
      console.error('Error fetching sidebar counts:', error);
      // Set counts to 0 on error to avoid breaking the UI
      setCounts({
        feedbackCount: 0,
        technicalNotesCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.current_workspace?.id) {
      fetchAllCounts();
    }
  }, [user?.current_workspace?.id]);

  return {
    counts,
    loading,
    refetch: fetchAllCounts,
    refetchFeedbackCount: fetchFeedbackCount,
    refetchTechnicalNotesCount: fetchTechnicalNotesCount,
  };
};
